# Enhanced Job Posting

Enhanced Job Posting uses a panel embedded within your software to help hirers post job ads on SEEK.
The panel infers SEEK-specific inputs based on your system’s job requisition to provide hirers with a pre-filled form to post their job ad.

## Before you begin

Enhanced Job Posting will roll out in phases, starting with partners in our Partner Program.
If we haven’t contacted you about this solution, do not commence your build until you receive confirmation that we can support the certification and release of your integration.
We’ll get in touch with our remaining partners once Enhanced Job Posting reaches general availability.

Your software requires a Job Posting relationship with a [test hirer account] to build and test Enhanced Job Posting.
Review the [Developer Dashboard hirers page] and [contact SEEK’s support team] to configure the relationship if it is missing.

To implement Enhanced Job Posting, your software will need to request both:

1. [Browser tokens](/auth/browser-tokens), for display of the panel, and
2. [Partner tokens](/auth/partner-tokens), for querying draft job ad details and posting job ads to SEEK.

Once you have completed your internal build and test phases, contact SEEK’s support team to certify your integration and gain access to Enhanced Job Posting for production hirer accounts.
This is a separate pre-release step to ensure integration compatibility and quality.

[contact SEEK’s support team]: https://au.employer.seek.com/contactus
[Developer Dashboard hirers page]: /manage/hirers
[test hirer account]: /introduction#integration-process

## Integration overview

The Enhanced Job Posting panel is intended to be displayed after the hirer has created a job requisition, and has selected to post a job ad to SEEK.
A typical workflow is:

```mermaid Enhanced Job Posting
   sequenceDiagram
   participant Hirer as Hirer
   participant Partner as Partner
   participant Panel as EJP Panel
   participant SEEK API as SEEK API

   Note over Hirer, SEEK API: Step 1 - Display panel
   Partner->>Panel: Call `render`
   Hirer->>Panel: Review / amend pre-filled details

   Note over Hirer, SEEK API: Step 2 - Manage drafts
   Hirer->>Partner: Select Save
   Partner->>Panel: Trigger save event
   Panel->>Partner: Return `draftPositionProfileId`

   Note over Hirer, SEEK API: Step 3 - Post a draft job ad
   Partner->>SEEK API: Post job ad

   Note over Hirer, SEEK API: Step 4 - Update a job ad from a draft
   Partner->>Panel: Call `render` with `positionProfileId`
   Hirer->>Panel: Review / amend pre-filled details
   Hirer->>Partner: Select Save
   Partner->>Panel: Trigger save event
   Panel->>Partner: Return new `draftPositionProfileId`
   Partner->>SEEK API: Update job ad
```

1. [Display panel]: Your software displays the Enhanced Job Posting panel, passing job requisition data which will be used to pre-fill the form.
   The hirer then reviews and amends details within the panel.
2. [Manage drafts]: The hirer continues in your software, which triggers the panel to save the draft job ad returning a `draftPositionProfileId`.
   This draft can be queried, previewed, or resubmitted to the render function to allow a hirer to continue editing their draft.
3. [Post a draft job ad]: When ready to post, your software calls the `postPositionFromDraft` mutation with the `draftPositionProfileId` to post the job ad to SEEK.
4. [Update a job ad from a draft]: To allow updates to existing job ads, your software uses the same workflow.
   The Enhanced Job Posting panel is rendered with the existing `positionProfileId` to allow editing.
   When ready to update, your software calls the `updatePositionFromDraft` mutation with the `draftPositionProfileId` to update the job ad on.

[Display panel]: /use-cases/job-posting/enhanced/display-panel
[Manage drafts]: /use-cases/job-posting/enhanced/manage-drafts
[Post a draft job ad]: /use-cases/job-posting/enhanced/post-a-draft-job-ad
[Update a job ad from a draft]: /use-cases/job-posting/enhanced/update-a-job-ad

## Placement and UX Guidelines

The Enhanced Job Posting panel should replace any SEEK-specific forms or fields in your existing job posting flow.
Display it to users after they have set general position details, selected a SEEK hirer account and any screening questions in your software.
If applicable these details can be collected by your software on the same screen as the panel.

If there is overlap between fields collected by your UI and the Enhanced Job Posting panel, you can signal this for some limited fields in the [`partnerManagedFields` prop](/use-cases/job-posting/enhanced/display-panel#props).
For example, if your software supports scheduling the start date of a job ad you may want to collect the scheduled start date and end date in your software outside of the panel.

If your software supports posting to multiple job boards on the same screen, keep each board’s fields in clearly separate sections.
SEEK’s Enhanced Job Posting panel should be visually distinct so hirers always know which inputs apply to which board.

### Display considerations

Enhanced Job Posting panel can be embedded inline (recommended), in a drawer, a popup or a separate screen.
Whatever you choose, ensure the container is wide enough to accommodate SEEK’s ad product comparison table, which is optimised for five columns side by side.

| Display option           | Container size recommendation        |
| ------------------------ | ------------------------------------ |
| Inline / separate screen | Embed at the full width of your UI   |
| Popup / drawer           | Use a minimum of 90% of screen width |

## Transitioning from Classic Job Posting

No migration steps are required.
Jobs posted via the Classic Job Posting flow are editable in the Enhanced Job Posting panel.
